import { createSelector } from 'reselect';
import utils from 'Utils';
import { getCategories } from '../basicData';
import {
  getNormalIndicators,
  getMegaIndicators,
  getRouteIndicators,
  getDocIndicators,
  getRouteIndicatorsActiveData,
} from './basic';

const { getIndicatorIds } = utils.array;

export const indicatorsWithState = createSelector(
  [getNormalIndicators, getMegaIndicators, getRouteIndicators, getCategories],
  (normal, mega, route, categories) => {
    // Builds array of indicators, megas, and non-mega categories used to construct table row data in `getRows.js`.
    const finalInds = [];
    route.forEach((fullString) => {
      // Indicators with category hashes.
      if (fullString.includes('|')) {
        const [indId, hash] = fullString.split('|');
        const activeMegas = Object.entries(mega)
          .filter(([id, obj]) => obj.children.includes(parseInt(indId, 10))); // eslint-disable-line no-unused-vars

        const filteredMega = activeMegas.find((meg) => {
          const megaId = parseInt(meg[0], 10);
          const foundCat = Object.values(categories)
            .find((cat) => cat.categoryHash === hash);

          return foundCat.indicators.includes(megaId);
        });

        if (filteredMega) {
          const [megaId, megaProps] = filteredMega;

          finalInds.push(
            // Mega-Indicator.
            {
              ...megaProps,
              children: megaProps.children
                .filter((child) => getIndicatorIds(route).includes(`${child}`)),
              id: megaId,
              fullId: fullString,
            },
            // Normal Indicator.
            {
              ...normal[indId],
              categories: megaProps.categories,
              id: indId,
              fullId: fullString,
              hasMega: megaId,
              sortId: `${megaId}-${indId}`,
            }
          );
        } else {
          const [catId, catProps] = Object.entries(categories)
            .find(([id, cat]) => cat.categoryHash === hash); // eslint-disable-line no-unused-vars

          finalInds.push(
            // Non-mega Category.
            {
              ...catProps,
              children: [parseInt(indId, 10)],
              id: catId,
              fullId: fullString,
              isCat: true,
            },
            // Normal Indicator.
            {
              ...normal[indId],
              categories: [parseInt(catId, 10)],
              id: indId,
              fullId: fullString,
              // Technically `hasCat`, but needed here for logic in getRows.
              hasMega: catId,
              sortId: `${catId}-${indId}`,
            }
          );
        }
      } else {
        // Indicators without category hashes.
        const indId = fullString;
        const activeMegas = Object.entries(mega)
          .filter(([id, obj]) => obj.children.includes(parseInt(indId, 10))); // eslint-disable-line no-unused-vars

        if (activeMegas && activeMegas.length > 1) {
          activeMegas.forEach((meg) => {
            const [megaId, megaProps] = meg;

            finalInds.push(
              // Mega-Indicator.
              {
                ...megaProps,
                children: megaProps.children
                  .filter((child) => getIndicatorIds(route).includes(`${child}`)),
                id: megaId,
                fullId: fullString,
              },
              // Normal Indicator.
              {
                ...normal[indId],
                categories: megaProps.categories,
                id: indId,
                fullId: fullString,
                hasMega: megaId,
                sortId: `${megaId}-${indId}`,
              }
            );
          });
        } else {
          // Normal Indicator without Mega parent.
          finalInds.push(
            {
              ...normal[indId],
              id: indId,
              hasMega: false,
            }
          )
        }
      }
    });

    // Remove duplicate megas/categories.
    const uniqueIds = new Set();
    return finalInds.filter((item) => {
      if (item.children) {
        const isDupe = uniqueIds.has(item.id);
        uniqueIds.add(item.id);

        return !isDupe;
      }
      return true;
    });
  }
);

export const indicatorsWithData = createSelector(
  [indicatorsWithState, getDocIndicators],
  (indicators, data) =>
    indicators.map((x) => (!data[x.id] ? x : ({ ...x, ...data[x.id] })))
);

export const indicatorsWithRouteState = createSelector(
  [getRouteIndicatorsActiveData, indicatorsWithData],
  (active, indicators) => indicators.map((x) => {
    if (x.fullId) {
      return (!active[x.fullId] ? x : ({ ...x, state: active[x.fullId] }));
    }
    return (!active[x.fullId] ? x : ({ ...x, state: active[x.fullId] }));
  })
);

export default {
  indicatorsWithState,
  indicatorsWithData,
  indicatorsWithRouteState,
};



// WEBPACK FOOTER //
// ./app/selectors/table/getIndicators.js